import { useContext, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormParserContext } from 'src/contexts/form.context';
import { InputContext } from 'src/contexts/input.context';
import { ifTrue } from 'src/tools/logic.tools';
import { requestContingencyLineItemsList } from 'src/store/change_orders/change_orders.action';
import { getChangeOrderCurrentPeriod } from 'src/store/change_orders/change_orders.getters';
import { getCurrentSubprojectCurrencySymbol } from 'src/store/subProject/subProject.getters';
import { Result, RecordObject, ButtonBaseType, ContingencyLineItem } from 'src/models';

import { SearchableNestedDropdown as SearchableNestedDropdownComponent } from 'src/components/InputRenderers';

export interface Props {
  isSearchable?: boolean;
}

export function NestedContingencyDropdown({ isSearchable }: Props) {
  const {
    value,
    script,
    onChange,
    error,
    isDisabled,
    readData: tradeId,
    additionalReadData,
  } = useContext(FormParserContext);

  const [contingencyLineItemsList, setContingencyLineItemsList] = useState<ContingencyLineItem[]>([]);

  const [placeholder, setPlaceholder] = useState(script.placeholder);

  const [isUpdate, setIsUpdate] = useState(false);

  const dispatch = useDispatch();

  const currencySymbol = useSelector(getCurrentSubprojectCurrencySymbol);

  const currentPeriod = useSelector(getChangeOrderCurrentPeriod);

  const items = useMemo<ButtonBaseType[]>(() => {
    const lineItems: ButtonBaseType[] = [];

    const checkingArray: Array<string> = [];

    contingencyLineItemsList.forEach((item: ContingencyLineItem) => {
      if (!checkingArray.includes(item.group_id)) {
        checkingArray.push(item.group_id);
        lineItems.push({ label: item.group, id: item.group_id });
      }
    });

    return lineItems;
  }, [contingencyLineItemsList]);

  const nestedItems = useMemo<RecordObject<ButtonBaseType[]>>(() => {
    const nestedItemsObj: RecordObject<ButtonBaseType[]> = {};

    for (const item of items) {
      const values = contingencyLineItemsList.filter((line: ContingencyLineItem) => line.group_id === item.id);

      nestedItemsObj[item.id] = values.map((el: ContingencyLineItem) => {
        return { ...el, label: `${el.description} (${currencySymbol + el.amount})`, sub_label: `${el.group} | ` };
      });
    }

    return nestedItemsObj;
  }, [contingencyLineItemsList, items, currencySymbol]);

  function handleResult(result: Result) {
    if (result.isOK) {
      setContingencyLineItemsList(result.payload);
    }
  }

  useEffect(() => {
    if (additionalReadData?.cost < 0) setPlaceholder('Select contingency line to credit');
    else setPlaceholder('Select contingency line to pull from');
  }, [additionalReadData]);

  useEffect(() => {
    if (tradeId && currentPeriod)
      dispatch(requestContingencyLineItemsList(tradeId, currentPeriod, { callback: handleResult }));
  }, [tradeId, dispatch, currentPeriod]);

  useEffect(() => {
    if (tradeId) {
      isUpdate ? onChange('') : setIsUpdate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeId, onChange]);

  useEffect(() => {
    if (additionalReadData?.is_contingency && value) onChange('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalReadData?.is_contingency, onChange]);

  return (
    <InputContext.Provider
      value={{
        classNames: { container: 'w-100p', input: ifTrue(error, 'with-error') },
        value,
        onChange,
        isDisabled: script.isDisabled || isDisabled,
        isAutoFocus: script.autofocus,
        placeholder: placeholder,
        items,
        nestedItems,
      }}
    >
      <SearchableNestedDropdownComponent isSearchable={isSearchable} />
    </InputContext.Provider>
  );
}
